import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/sonner';
import { useTranslation } from 'react-i18next';

export interface Notification {
  id: string;
  user_id: string;
  type: 'group_invitation' | 'user_joined_group' | 'new_group_member' | 'tournament_created' | 'tournament_opened' | 'tournament_closed' | 'tournament_reminder' | 'tournament_matches_ready' | 'match_validation' | 'match_validated' | 'group_announcement' | 'group_message' | 'private_message' | 'security_alert' | 'tournament_team_invitation' | 'tournament_team_response' | 'availability_poll_created' | 'availability_poll_closed' | 'availability_poll_cancelled' | 'match_scheduled' | 'match_cancelled' | 'match_modified' | 'event_reminder_24h' | 'event_reminder_1h' | 'challenge_received' | 'challenge_cancelled' | 'challenge_scheduled' | 'challenge_reminder' | 'upcoming_match_reminder' | 'score_capture_reminder' | 'ladder_decay' | 'open_match_available' | 'competitive_digest' | 'rank_movement';
  title: string;
  message: string;
  content: any;
  is_read: boolean;
  created_at: string;
  updated_at: string;
}

export const useNotifications = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Fetch notifications
  const fetchNotifications = async () => {
    if (!user) return;
    
    try {
      
      const { data, error } = await supabase
        .from('notifications')
        .select('id, user_id, type, title, message, content, is_read, created_at, updated_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform the data to match our interface
      const transformedData: Notification[] = (data || []).map(item => ({
        id: item.id,
        user_id: item.user_id,
        type: item.type as Notification['type'],
        title: item.title,
        message: item.message,
        content: item.content,
        is_read: item.is_read,
        created_at: item.created_at,
        updated_at: item.updated_at || item.created_at
      }));

      setNotifications(transformedData);
      setUnreadCount(transformedData.filter(n => !n.is_read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId: string) => {
    if (!user) return;
    
    // Optimistically update local state first
    setNotifications(prev =>
      prev.map(n =>
        n.id === notificationId ? { ...n, is_read: true } : n
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, updated_at: new Date().toISOString() })
        .eq('id', notificationId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error marking notification as read:', error);
        // Revert optimistic update on error
        setNotifications(prev =>
          prev.map(n =>
            n.id === notificationId ? { ...n, is_read: false } : n
          )
        );
        setUnreadCount(prev => prev + 1);
        toast.error('Failed to update notification');
      } else {
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Revert optimistic update on error
      setNotifications(prev =>
        prev.map(n =>
          n.id === notificationId ? { ...n, is_read: false } : n
        )
      );
      setUnreadCount(prev => prev + 1);
      toast.error('Failed to update notification');
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    if (!user) return;

    // Get current unread notifications
    const unreadNotifications = notifications.filter(n => !n.is_read);
    
    // Optimistically update local state first
    setNotifications(prev =>
      prev.map(n => ({ ...n, is_read: true }))
    );
    setUnreadCount(0);

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true, updated_at: new Date().toISOString() })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) {
        console.error('Error marking all notifications as read:', error);
        // Revert optimistic update on error
        setNotifications(prev =>
          prev.map(n => {
            const wasUnread = unreadNotifications.find(un => un.id === n.id);
            return wasUnread ? { ...n, is_read: false } : n;
          })
        );
        setUnreadCount(unreadNotifications.length);
        toast.error('Failed to update notifications');
      } else {
        toast.success('All notifications marked as read');
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      // Revert optimistic update on error
      setNotifications(prev =>
        prev.map(n => {
          const wasUnread = unreadNotifications.find(un => un.id === n.id);
          return wasUnread ? { ...n, is_read: false } : n;
        })
      );
      setUnreadCount(unreadNotifications.length);
      toast.error('Failed to update notifications');
    }
  };

  // Clear all notifications permanently
  const clearAllNotifications = async () => {
    if (!user) return;

    const previousNotifications = notifications;

    // Optimistically clear local state
    setNotifications([]);
    setUnreadCount(0);

    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('user_id', user.id);

      if (error) throw error;

      toast.success(t('notifications.allCleared'));
    } catch (error) {
      console.error('Error clearing notifications:', error);
      setNotifications(previousNotifications);
      setUnreadCount(previousNotifications.filter(n => !n.is_read).length);
      toast.error(t('notifications.clearFailed'));
    }
  };

  // Enhanced notification handler for push notifications
  const handleNewNotification = (notification: Notification) => {
    // Add to local state
    setNotifications(prev => [notification, ...prev]);
    if (!notification.is_read) {
      setUnreadCount(prev => prev + 1);
    }

    // Show browser notification if page is hidden and permission is granted
    if (typeof document !== 'undefined' && 
        document.visibilityState === 'hidden' && 
        'Notification' in window && 
        Notification.permission === 'granted') {
      
      try {
        // Show local notification as fallback
        new Notification(notification.title, {
          body: notification.message,
          icon: '/pwa-192x192.png',
          badge: '/pwa-64x64.png',
          tag: notification.type
        });
      } catch (error) {
        console.error('Error showing local notification:', error);
      }
    }

    // Show in-app toast for different notification types
    if (notification.type === 'security_alert') {
      toast.error(notification.title, {
        description: notification.message,
        duration: 10000, // Longer duration for security alerts
      });
    } else if (notification.type === 'tournament_team_invitation') {
      toast.info(notification.title, {
        description: notification.message,
        duration: 8000,
      });
    } else if (notification.type === 'tournament_team_response') {
      toast.success(notification.title, {
        description: notification.message,
        duration: 6000,
      });
    } else if (notification.type === 'private_message') {
      toast.info(notification.title, {
        description: notification.message,
        duration: 5000,
      });
    } else if (notification.type === 'availability_poll_created') {
      toast.info(notification.title, {
        description: notification.message,
        duration: 6000,
      });
    } else if (notification.type === 'availability_poll_closed') {
      toast.info(notification.title, {
        description: notification.message,
        duration: 6000,
      });
    } else if (notification.type === 'availability_poll_cancelled') {
      toast.warning(notification.title, {
        description: notification.message,
        duration: 6000,
      });
    } else if (notification.type === 'ladder_decay') {
      toast.warning(notification.title, {
        description: notification.message,
        duration: 7000,
      });
    } else {
      toast.info(notification.title, {
        description: notification.message
      });
    }
  };

  // Real-time subscription
  useEffect(() => {
    if (!user) {
      // Don't set up subscriptions without authenticated user
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    fetchNotifications();

    // Use unique channel name per component instance to avoid conflicts
    const channelName = `notifications-${user.id}-${Date.now()}-${Math.random()}`;
    
    let channel: any = null;

    try {
      // Clean up any existing notification channels for this user
      const existingChannels = supabase.getChannels();
      const userNotificationChannels = existingChannels.filter(ch => ch.topic.startsWith(`notifications-${user.id}-`));
      userNotificationChannels.forEach(channel => {
        supabase.removeChannel(channel);
      });

      // Set up real-time subscription with better error handling
      channel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            try {
              if (payload.eventType === 'INSERT') {
                const newNotification = payload.new as any;
                const transformedNotification: Notification = {
                  id: newNotification.id,
                  user_id: newNotification.user_id,
                  type: newNotification.type,
                  title: newNotification.title,
                  message: newNotification.message,
                  content: newNotification.content,
                  is_read: newNotification.is_read,
                  created_at: newNotification.created_at,
                  updated_at: newNotification.updated_at || newNotification.created_at
                };
                
                handleNewNotification(transformedNotification);
                
              } else if (payload.eventType === 'UPDATE') {
                const updatedNotification = payload.new as any;
                const transformedNotification: Notification = {
                  id: updatedNotification.id,
                  user_id: updatedNotification.user_id,
                  type: updatedNotification.type,
                  title: updatedNotification.title,
                  message: updatedNotification.message,
                  content: updatedNotification.content,
                  is_read: updatedNotification.is_read,
                  created_at: updatedNotification.created_at,
                  updated_at: updatedNotification.updated_at || updatedNotification.created_at
                };
                
                setNotifications(prev => {
                  const updated = prev.map(n =>
                    n.id === transformedNotification.id ? transformedNotification : n
                  );
                  return updated;
                });
                
                // Update unread count if read status changed
                if (updatedNotification.is_read && payload.old && !payload.old.is_read) {
                  setUnreadCount(prev => Math.max(0, prev - 1));
                }
              } else if (payload.eventType === 'DELETE') {
                const deletedId = payload.old.id;
                setNotifications(prev => prev.filter(n => n.id !== deletedId));
              }
            } catch (error) {
              console.error('❌ Error processing notification change:', error);
            }
          }
        )
        .subscribe();

    } catch (error) {
      console.error('❌ Error setting up notifications subscription:', error);
      // Fallback to polling if real-time fails
      const pollInterval = setInterval(() => {
        fetchNotifications();
      }, 30000); // Poll every 30 seconds

      return () => {
        clearInterval(pollInterval);
      };
    }

    return () => {
      if (channel) {
        try {
          supabase.removeChannel(channel);
        } catch (error) {
          console.error('❌ Error removing notifications channel:', error);
        }
      }
    };
  }, [user]);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    clearAllNotifications,
    refetch: fetchNotifications
  };
};
